import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ErrorMessage } from '@/components/ui/error-message';
import { nameSchema, emailSchema } from '@/lib/validationSchemas';

const validationSchema = Yup.object().shape({
  name: nameSchema,
  ownerEmail: emailSchema,
  createDefaultWorkspace: Yup.boolean().default(true),
});

type FormData = Yup.InferType<typeof validationSchema>;

const defaultValues: FormData = {
  name: '',
  ownerEmail: '',
  createDefaultWorkspace: true,
};

interface CreateOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string, ownerEmail: string, createDefaultWorkspace: boolean) => void;
  loading?: boolean;
}

export function CreateOrganizationDialog({
  open,
  onOpenChange,
  onSubmit,
  loading,
}: CreateOrganizationDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const onFormSubmit = (data: FormData) => {
    onSubmit(data.name.trim(), data.ownerEmail.trim().toLowerCase(), data.createDefaultWorkspace ?? true);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset(defaultValues);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>
              Create a new organization. The owner must be an existing platform user.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name</Label>
              <Input
                id="org-name"
                placeholder="Acme Corp"
                autoFocus
                {...register('name')}
                hasError={!!errors.name}
                disabled={loading}
              />
              <ErrorMessage message={errors.name?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="owner-email">Owner Email</Label>
              <Input
                id="owner-email"
                type="email"
                placeholder="owner@example.com"
                {...register('ownerEmail')}
                hasError={!!errors.ownerEmail}
                disabled={loading}
              />
              <ErrorMessage message={errors.ownerEmail?.message} />
              <p className="text-xs text-muted-foreground">Must be an existing platform user</p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="create-workspace"
                checked={watch('createDefaultWorkspace')}
                onCheckedChange={(checked) => setValue('createDefaultWorkspace', !!checked)}
                disabled={loading}
              />
              <Label htmlFor="create-workspace" className="cursor-pointer font-normal">
                Create default workspace
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Organization'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
